import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  User, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../config/firebase';
import axios from 'axios';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Function to login with email and password
  const login = (email: string, password: string) => {
    return signInWithEmailAndPassword(auth, email, password)
      .then(async (userCredential) => {
        // Get and store the user token
        const token = await userCredential.user.getIdToken();
        localStorage.setItem('authToken', token);
        
        // After successful login, update user profile in our backend
        updateUserProfileInBackend(userCredential.user);
        return userCredential.user;
      });
  };

  // Function to register a new user
  const register = (email: string, password: string) => {
    return createUserWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        // After successful registration, create user profile in our backend
        updateUserProfileInBackend(userCredential.user);
        return userCredential.user;
      });
  };

  // Function to logout
  const logout = () => {
    // Remove the token from localStorage
    localStorage.removeItem('authToken');
    return signOut(auth);
  };

  // Function to reset password
  const resetPassword = (email: string) => {
    return sendPasswordResetEmail(auth, email);
  };

  // Function to update user profile in our backend
  const updateUserProfileInBackend = async (user: User) => {
    try {
      const token = await user.getIdToken();
      
      await axios.post(
        `http://localhost:5000/api/auth/user-profile`,
        {
          displayName: user.displayName,
          photoURL: user.photoURL
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
    } catch (error) {
      console.error('Error updating user profile in backend:', error);
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      // If user is logged in, get and store their token
      if (user) {
        try {
          const token = await user.getIdToken();
          localStorage.setItem('authToken', token);
          console.log('AuthContext: Token atualizado no localStorage');
        } catch (error) {
          console.error('Error getting user token:', error);
        }
      } else {
        // If user is logged out, remove the token
        localStorage.removeItem('authToken');
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Renovar o token periodicamente (a cada 50 minutos)
  useEffect(() => {
    if (!currentUser) return;

    const refreshToken = async () => {
      try {
        const token = await currentUser.getIdToken(true); // Force refresh
        localStorage.setItem('authToken', token);
        console.log('AuthContext: Token renovado automaticamente');
      } catch (error) {
        console.error('Error refreshing token:', error);
      }
    };

    // Renovar imediatamente
    refreshToken();

    // Configurar intervalo para renovar a cada 50 minutos (3000000 ms)
    const intervalId = setInterval(refreshToken, 50 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [currentUser]);

  const value = {
    currentUser,
    loading,
    login,
    register,
    logout,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
